import { appStore } from '@app/AppStore';
import DateTimeService from '@app/Services/DateTimeService';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';
import { RosterCell } from '@app/Models/RosterCell';
import { DutyItem, FunctionModel } from '@app/Models/WebApiModels';
import { IPersonStore } from "@app/Components/VirtualizedRoster";
import { permissionStore } from '@app/Stores/PermissionStore';

export interface IWindowAdal extends Window {
    adal?: {
        redirectToLogin(): void;
        redirectToLogout(): void;
    };
}

export class WritePermissionOptions {
    type: "WritePermissionOptions";
    personStore?: IPersonStore;
    personFunctionId?: string;
    ignorePermissions?: number[];
}

export class AzureAuth {
    static logOut = (all?: boolean) => {
        appStore.clearUserData();
        const appWindow: IWindowAdal = window;
        all && appStore.setValue('logout-event', 'logout' + Math.random());
        appWindow.adal && appWindow.adal.redirectToLogout();
    };

    static exitImpersonation = () => {
        appStore.exitImpersonation();
    };

    static login = () => {
        appStore.clearUserData();
        const appWindow: IWindowAdal = window;
        appWindow.adal && appWindow.adal.redirectToLogin();
    };

    public static hasAccess(permissions: number | number[]) {
        const hasAdmin = permissionStore.currentUserPermissions.includes(UserPermissions.AdminToolsWrite);
        if (hasAdmin) return true;

        if (typeof permissions === 'number') {
            return permissionStore.currentUserPermissions.indexOf(permissions) !== -1;
        }

        if (Array.isArray(permissions)) {
            for (const permission of permissions) {
                if (permissionStore.currentUserPermissions.indexOf(permission) !== -1) {
                    return true;
                }
            }
        }
        return false;
    }

    static get userUpn() {
        return appStore.currentUser?.unique_name || null;
    }

    static isAllowChangeForDate(date: Date) {
        const hasAccess = appStore.isRosterWriteUnlocked;
        if (hasAccess) {
            return true;
        }
        const dateNow = DateTimeService.today();
        const currentDay = dateNow.getUTCDate();
        const startOfMonth = DateTimeService.toFirstDayOfMonth(dateNow);
        const startOfPrevMonth = DateTimeService.addMonths(startOfMonth, -1);
        return !((currentDay >= 3 && DateTimeService.isBeforeDate(date, startOfMonth)) || DateTimeService.isBeforeDate(date, startOfPrevMonth));
    }

    static hasRosterAccess() {
        const rosterPermissions = [
            UserPermissions.RosterCabinRead,
            UserPermissions.RosterCockpitRead,
            UserPermissions.RosterGroundRead,
            UserPermissions.RosterFseRead,
            UserPermissions.RosterMarshalRead];

        return AzureAuth.hasAccess(rosterPermissions);
    }

    static hasCrewPortalAccess(functionId?: string | null) {

        if (typeof functionId === 'undefined') {
            return AzureAuth.hasAccess([
                UserPermissions.CrewPortalAccess,
                UserPermissions.ReadFDRCockpit,
                UserPermissions.ReadFDRCockpitAeropers,
                UserPermissions.ReadFDRCabin,
                UserPermissions.ReadFDRCabinEFA,
                UserPermissions.ReadFDRGround,
                UserPermissions.CrewPortalCockpitReadOthers,
                UserPermissions.CrewPortalCockpitAeropersReadOthers,
                UserPermissions.CrewPortalCabinEFAReadOthers,
                UserPermissions.CrewPortalCabinReadOthers,
                UserPermissions.CrewPortalGroundReadOthers,
                UserPermissions.CrewPortalCockpitCanControlNbu,
                UserPermissions.CrewPortalCabinCanControlNbu
            ]);
        }

        if (functionId === 'PIC' || functionId === 'FO' || functionId === FunctionGroupIds.Cockpit) {
            return AzureAuth.hasAccess([
                UserPermissions.ReadFDRCockpit,
                UserPermissions.ReadFDRCockpitAeropers,
                UserPermissions.CrewPortalCockpitReadOthers,
                UserPermissions.CrewPortalCockpitAeropersReadOthers,
                UserPermissions.CrewPortalCockpitCanControlNbu,
                UserPermissions.CrewPortalCabinCanControlNbu
            ]);
        }
        if (functionId === 'CC' || functionId === 'SC' || functionId === FunctionGroupIds.Cabin) {
            return AzureAuth.hasAccess([
                UserPermissions.ReadFDRCabin,
                UserPermissions.CrewPortalCabinReadOthers,
                UserPermissions.ReadFDRCabinEFA,
                UserPermissions.CrewPortalCabinEFAReadOthers
            ]);
        }
        if (functionId === 'OFC' || functionId === FunctionGroupIds.Ground) {
            return AzureAuth.hasAccess([
                UserPermissions.ReadFDRGround,
                UserPermissions.CrewPortalGroundReadOthers
            ]
            );
        }
        return AzureAuth.hasAccess(UserPermissions.CrewPortalAccess);
    }

    static canManageCrewPortal(isCockpit: boolean, isCabin: boolean, isGround: boolean, isOwnData?: boolean) {
        return !!isOwnData
            || isCockpit && AzureAuth.hasAccess(UserPermissions.CrewPortalCockpitWriteOthers)
            || isCabin && AzureAuth.hasAccess(UserPermissions.CrewPortalCabinWriteOthers)
            || isGround && AzureAuth.hasAccess(UserPermissions.CrewPortalGroundWriteOthers);
    }

    static getRosterPermissions = () => [
        UserPermissions.RosterCabinRead,
        UserPermissions.RosterCockpitRead,
        UserPermissions.RosterGroundRead,
        UserPermissions.RosterFseRead,
        UserPermissions.RosterMarshalRead
    ];


    static hasReadHistoryPermission(cells: RosterCell[], personStore: IPersonStore, rosterFunctionGroupId: string, focusedDuty: DutyItem | null) {
        if (!cells.length) {
            return false;
        }

        for (const cell of cells) {
            const personData = personStore.getDetails(cell.person.id);
            const pf = personData?.function;
            const hasNoContract = focusedDuty?.hasNoContract;

            if (!hasNoContract && !pf) {
                return false;
            }

            if (!hasNoContract && pf && !pf.functionGroupId && !pf.planningFunctionGroupId) {
                return false;
            }

            const rosterReadPermissions: number[][] = [];
            if (pf?.functionGroupId === FunctionGroupIds.Cockpit || pf?.planningFunctionGroupId === FunctionGroupIds.Cockpit || (hasNoContract && rosterFunctionGroupId === FunctionGroupIds.Cockpit)) {
                rosterReadPermissions.push([UserPermissions.SeeRosterHistoryCockpit]);
            }
            if (pf?.functionGroupId === FunctionGroupIds.Cabin || pf?.planningFunctionGroupId === FunctionGroupIds.Cabin || (hasNoContract && rosterFunctionGroupId === FunctionGroupIds.Cabin)) {
                rosterReadPermissions.push([UserPermissions.SeeRosterHistoryCabin]);
            }
            if (pf?.functionGroupId === FunctionGroupIds.Ground || pf?.planningFunctionGroupId === FunctionGroupIds.Ground || (hasNoContract && rosterFunctionGroupId === FunctionGroupIds.Ground)) {
                rosterReadPermissions.push([UserPermissions.SeeRosterHistoryGround]);
            }
            if (pf?.functionGroupId === FunctionGroupIds.Fse || pf?.planningFunctionGroupId === FunctionGroupIds.Fse || (hasNoContract && rosterFunctionGroupId === FunctionGroupIds.Fse)) {
                rosterReadPermissions.push([UserPermissions.SeeRosterHistoryFse]);
            }
            if (pf?.functionGroupId === FunctionGroupIds.Marshals || pf?.planningFunctionGroupId === FunctionGroupIds.Marshals || (hasNoContract && rosterFunctionGroupId === FunctionGroupIds.Marshals)) {
                rosterReadPermissions.push([UserPermissions.SeeRosterHistoryMarshal]);
            }

            for (const permissionId of rosterReadPermissions) {
                if (!AzureAuth.hasAccess(permissionId)) {
                    return false;
                }
            }
        }

        return true;
    }

    static hasRosterWritePermission(cells: RosterCell[], options: WritePermissionOptions, rosterFunctionGroupId: string, onReleasedCheck?: (date: Date, functionGroupId: string | null, functionId?: string | null) => boolean, ignorePastCheck?: boolean) {
        if (!cells.length) {
            return false;
        }

        for (const cell of cells) {
            let personFunction: FunctionModel = {} as FunctionModel;

            if (options.personFunctionId) {
                personFunction.functionGroupId = options.personFunctionId;
                personFunction.planningFunctionGroupId = options.personFunctionId;
            } else if (options.personStore) {
                const personData = options.personStore.getDetails(cell.person.id);
                if (personData?.function) {
                    personFunction = personData.function;
                } else if (rosterFunctionGroupId) {
                    //in case no function found - failback to selected view
                    personFunction.functionGroupId = rosterFunctionGroupId;
                    personFunction.planningFunctionGroupId = rosterFunctionGroupId;
                }
            }

            if (!personFunction?.functionGroupId && !personFunction?.planningFunctionGroupId) {
                return false;
            }

            const isDateReleased = onReleasedCheck?.(cell.date, personFunction.functionGroupId ?? personFunction.planningFunctionGroupId ?? null, personFunction?.planningFunctionId);

            let rosterWritePermissions: number[][] = [];

            if ((rosterFunctionGroupId === FunctionGroupIds.Cockpit || rosterFunctionGroupId === FunctionGroupIds.Fcm)
                && (personFunction.functionGroupId === FunctionGroupIds.Cockpit || personFunction.planningFunctionGroupId === FunctionGroupIds.Cockpit)) {
                if (isDateReleased) {
                    rosterWritePermissions.push([UserPermissions.RosterCockpitWriteTracking, UserPermissions.RosterCockpitWritePlanning]);
                } else {
                    rosterWritePermissions.push([UserPermissions.RosterCockpitWritePlanning]);
                }
            }
            if ((rosterFunctionGroupId === FunctionGroupIds.Cabin || rosterFunctionGroupId === FunctionGroupIds.Fcm)
                && (personFunction.functionGroupId === FunctionGroupIds.Cabin || personFunction.planningFunctionGroupId === FunctionGroupIds.Cabin)) {
                if (isDateReleased) {
                    rosterWritePermissions.push([UserPermissions.RosterCabinWriteTracking, UserPermissions.RosterCabinWritePlanning]);
                } else {
                    rosterWritePermissions.push([UserPermissions.RosterCabinWritePlanning]);
                }
            }
            if (rosterFunctionGroupId === FunctionGroupIds.Ground
                && (personFunction.functionGroupId === FunctionGroupIds.Ground || personFunction.planningFunctionGroupId === FunctionGroupIds.Ground)) {
                if (isDateReleased) {
                    rosterWritePermissions.push([UserPermissions.RosterGroundWriteTracking, UserPermissions.RosterGroundWritePlanning]);
                } else {
                    rosterWritePermissions.push([UserPermissions.RosterGroundWritePlanning]);
                }
            }
            if (rosterFunctionGroupId === FunctionGroupIds.Fse
                && (personFunction.functionGroupId === FunctionGroupIds.Fse || personFunction.planningFunctionGroupId === FunctionGroupIds.Fse)) {
                rosterWritePermissions.push([UserPermissions.RosterFseWrite]);
            }
            if (rosterFunctionGroupId === FunctionGroupIds.Marshals
                && (personFunction.functionGroupId === FunctionGroupIds.Marshals || personFunction.planningFunctionGroupId === FunctionGroupIds.Marshals)) {
                rosterWritePermissions.push([UserPermissions.RosterMarshalWrite]);
            }

            if (options?.ignorePermissions && options.ignorePermissions.length > 0)
                rosterWritePermissions = rosterWritePermissions.map(permissions => permissions.filter(permission => options?.ignorePermissions?.findIndex(x => x === permission) === -1));

            if (!rosterWritePermissions.length) {
                return false;
            }

            for (const permissionId of rosterWritePermissions) {
                if (!AzureAuth.hasAccess(permissionId)) {
                    return false;
                }
            }

            if (!AzureAuth.isAllowChangeForDate(new Date(cell.date)) && !ignorePastCheck) {
                return false;
            }
        }

        return true;
    }

}

export enum UserPermissions {
    AdminToolsWrite = 1,
    AdminToolsRead = 2,
    AdminToolsSafe = 3,
    FullPermissionImpersonate = 5,
    LimitedPermissionImpersonate = 6,
    ChangeLogRead = 8,
    ChangeLogWrite = 9,
    RosterCabinRead = 10,
    RosterCabinWritePlanning = 11,
    RosterCabinWriteTracking = 12,
    RosterCabinRelease = 13,
    RosterCockpitRead = 15,
    RosterCockpitWritePlanning = 16,
    RosterCockpitWriteTracking = 17,
    RosterCockpitRelease = 18,
    RosterGroundRead = 20,
    RosterGroundWritePlanning = 21,
    RosterGroundWriteTracking = 22,
    RosterGroundRelease = 23,
    RosterGroundReport = 24,
    RosterMarshalRead = 25,
    RosterMarshalWrite = 26,
    RosterFseRead = 27,
    RosterFseWrite = 28,
    RosterWriteDuringPaymentCycle = 29,
    JCRImportRead = 30,
    JCRImportWrite = 31,
    RosterFseRelease = 33,
    RosterMarshalsRelease = 34,
    HidePlanningPeriod = 35,
    RosterReleasedPlanMinorChanges = 36,
    RosterGroundLimitedWrite = 37,
    RosterGroundTimeReportOtherWrite = 38,
    RosterGroundFlightOtherWrite = 39,
    ToolHotelManagement = 40,
    RosterGroundDutyTimeOtherWrite = 41,
    ToolEfaRead = 43,
    ToolEfaWrite = 44,
    ToolAeroPersRead = 45,
    ToolAeroPersWrite = 46,
    RotationToolRead = 47,
    RotationToolWrite = 48,
    EmailsRead = 49,
    LicenseManagementRead = 53,
    LicenseManagementWrite = 54,
    RequestCapacityTool = 55,
    HideDutyRead = 57,
    HideDutyWrite = 58,
    BlockingDaysFlightsRead = 60,
    BlockingDaysFlightsWrite = 61,
    CrewApiRead = 62,
    CrewApiWrite = 63,
    GenDecRead = 67,
    GenDecWrite = 68,
    GenDecManage = 69,
    DutyRelatedReportsRead = 70,
    ReportAreaRouteCompetenceManage = 71,
    ExperienceCorrection = 72,
    CateringReportRead = 73,
    CateringReportWrite = 74,
    TzvYearOverviewComment = 75,
    CrewPortalAccess = 80,
    ReadFDRCockpit = 81,
    ReadFDRCabin = 82,
    ReadFDRGround = 83,
    ReadFDRCockpitExtended = 84,
    ReadFDRCabinExtended = 85,
    ReadFDRGroundExtended = 86,
    ReadFDRCockpitAeropers = 87,
    ReadFDRCabinEFA = 88,
    FlightCrewRead = 90,
    FlightCrewUpdate = 91,
    FlightCommentRead = 93,
    FlightCommentWrite = 94,
    ContactWithPerson = 97,
    ReadPersonDetails = 100,
    ReadPersonDetailsAeropers = 101,
    ReadPersonDetailsCockpit = 102,
    ReadPersonDetailsInactive = 103,
    ReadPersonDetailsCabin = 104,
    MarshalsInfoRead = 105,
    MarshalsInfoWrite = 106,
    ReadPersonDetailsGround = 107,
    ReadPersonDetailsFse = 108,
    ReadPersonDetailsMarshal = 109,
    VacManagementCockpitAdmin = 110,
    VacManagementCabinAdmin = 111,
    VacManagementGroundAdmin = 112,
    VacManagementRead = 113,
    TemplateManagementRead = 115,
    TemplateManagementWrite = 116,
    ReadPersonDetailsEFA = 117,
    ReserveManagementRead = 118,
    ReserveManagementWrite = 119,
    CrewPortalCockpitReadOthers = 120,
    CrewPortalCabinReadOthers = 121,
    CrewPortalGroundReadOthers = 122,
    CrewPortalCockpitWriteOthers = 125,
    CrewPortalCabinWriteOthers = 126,
    CrewPortalGroundWriteOthers = 127,
    CrewPortalCockpitAeropersReadOthers = 128,
    CrewPortalCabinEFAReadOthers = 129,
    RosterCockpitReadWish = 130,
    RosterCabinReadWish = 131,
    RosterGroundReadWish = 132,
    SeeRosterHistoryCockpit = 135,
    SeeRosterHistoryCabin = 136,
    SeeRosterHistoryGround = 137,
    SeeRosterHistoryFse = 138,
    SeeRosterHistoryMarshal = 139,
    CrewPortalCockpitCanControlNbu = 140,
    CrewPortalCabinCanControlNbu = 141,
    RosterCockpitReadPP = 145,
    RosterCabinReadPP = 146,
    RosterGroundReadPP = 147,
    RosterMarshalReadPP = 148,
    RosterFseReadPP = 149,
    CommentEmployeeCockpitRead = 150,
    CommentEmployeeCabinRead = 151,
    CommentEmployeeGroundRead = 152,
    FseInfoRead = 155,
    FseInfoWrite = 156,
    SeniorityReportRead = 160,
    SeniorityReportWrite = 161,
    ReleaseNotesRead = 162,
    ReleaseNotesWrite = 163,
    CourseManagementRead = 164,
    CourseManagementWrite = 165,
    QualityLoopExportWrite = 170,
    QualityLoopExportReadPlan = 171,
    QualityLoopExportReadIdMapping = 172,
    QualityLoopExportOverride = 173,
    AzureAiExplainValidation = 175,
    PersonLicenseValidationSuppressRead = 180,
    PersonLicenseValidationSuppressWrite = 181
}